import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ScreenSizeService } from './screen-size.service';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { shuffle } from 'lodash';
import { environment } from 'src/environments/environment';
import {
    BannersJSON,
    BannerData,
    BannerSection,
    BannerImage,
    BannerPlacement,
} from '@app/model/banner.model';


@Injectable({
    providedIn: 'root',
})
export class BannerImageService {
    isMobile$ = this.screenSizeService.isMobileWidth;
    private readonly s3bannerUrl = this.resolveS3BannerUrl();

    private resolveS3BannerUrl(): string {
        const base = 'https://marideal-partners-';
        const suffix =
            window.location.hostname.includes('localhost') ||
            !environment.production
                ? 'dev'
                : 'prod';

        return `${base}${suffix}.s3.ap-southeast-1.amazonaws.com/banner-images-frontend/banners.json`;
    }

    private bannersSubject = new BehaviorSubject<Record<string, any> | null>(
        null
    );
    banners$ = this.bannersSubject.asObservable(); // Observable to listen for changes

    constructor(
        private screenSizeService: ScreenSizeService,
        private http: HttpClient
    ) {
        this.loadBannersFromS3();
    }

    // lodash shuffle algorithm to randomize an array

    private shuffleArray<T>(array: T[]): T[] {
        return shuffle(array);
    }

    loadBannersFromS3(): void {
        this.http.get<BannersJSON>(this.s3bannerUrl).subscribe(
            (data) => {
                Object.keys(data).forEach((sectionKey) => {
                    Object.keys(data[sectionKey]).forEach((bannerKey) => {
                        const banner = data[sectionKey][bannerKey];


                        if (
                            'images' in banner &&
                            Array.isArray(banner.images)
                        ) {
                            banner.images = this.shuffleArray(banner.images);
                        }
                    });
                });
https://chatgpt.com/codex
                this.bannersSubject.next(data);
            },
            (error) => {
                this.bannersSubject.next(null);
            }
        );
    }

    /** Get banners reactively (returns `Observable` to update live) */
    getBanners<T extends BannerData | BannerSection = BannerData>(
        page: string
    ): Observable<Record<string, T>> {
        return this.banners$.pipe(
            map((banners) => (banners?.[page] ?? {}) as Record<string, T>)
        );
    }

    /** Get a specific banner dynamically.
     * Returns the image URL of the first image in the images array.
     */
    getBanner(
        page: string,
        bannerKey: string,
        isMobile: boolean
    ): Observable<string> {
        return this.banners$.pipe(
            map(
                (banners) =>
                    banners?.[page]?.[bannerKey]?.images?.[0]?.[
                        isMobile ? 'mobile' : 'desktop'
                    ] || ''
            )
        );
    }

    /** Redirect to a banner link.
     * Uses the link of the first image in the images array.
     */
    redirectToLink(page: string, bannerKey: string): void {
        const banners = this.bannersSubject.getValue();
        const link = banners?.[page]?.[bannerKey]?.images?.[0]?.link;
        if (link) {
            window.open(link, '_blank');
        }
    }
    getBannersByPosition(
        page: string,
        position: string
    ): Observable<BannerData[]> {
        return this.getBanners(page).pipe(
            map((section) =>
                Object.values(section).filter(
                    (banner) => banner.position === position
                )
            )
        );
    }
}
